(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;
  const summaryCache = new Map();

  function formatReviewCountLabel(count, template, oneTemplate = '') {
    const safeCount = Math.max(0, Number(count) || 0);
    const labelTemplate = String((safeCount === 1 && oneTemplate) || template || '__COUNT__ ratings');

    if (!safeCount) {
      return '';
    }

    if (labelTemplate.includes('__COUNT__')) {
      return labelTemplate.replace('__COUNT__', String(safeCount));
    }

    if (labelTemplate.includes('{{ count }}')) {
      return labelTemplate.replace('{{ count }}', String(safeCount));
    }

    return `${safeCount} ratings`;
  }

  async function loadStoreSummary(appKey, secret = '') {
    const yotpo = root.JFTYotpo || {};
    const cacheKey = `${appKey}|${secret ? 'admin' : 'widget'}`;

    if (!appKey) {
      throw new Error('Yotpo summary unavailable');
    }

    if (summaryCache.has(cacheKey)) {
      return summaryCache.get(cacheKey);
    }

    const request = (async () => {
      let averageScore = 0;
      let totalReviews = 0;

      if (yotpo.fetchReviewsPage) {
        const { bottomline, pagination } = await yotpo.fetchReviewsPage(appKey, {
          page: 1,
          perPage: 1,
        });

        averageScore = Number(
          bottomline.average_score
          ?? bottomline.averageScore
        ) || 0;

        totalReviews = Number(
          bottomline.total_review
          ?? bottomline.totalReview
          ?? pagination.total
        ) || 0;
      }

      const trimmedSecret = String(secret || '').trim();
      if (trimmedSecret && typeof yotpo.fetchAccountReviewTotal === 'function') {
        try {
          const adminTotal = await yotpo.fetchAccountReviewTotal(appKey, trimmedSecret);
          if (adminTotal > 0) {
            totalReviews = adminTotal;
          }
        } catch (error) {
          // Keep widget total when Admin API is blocked.
        }
      }

      return { totalReviews, averageScore };
    })().catch((error) => {
      summaryCache.delete(cacheKey);
      throw error;
    });

    summaryCache.set(cacheKey, request);
    return request;
  }

  function registerYotpoStoreSummary() {
    if (!root.document || !root.Alpine) {
      return;
    }

    root.Alpine.data('yotpoStoreRating', (config = {}) => ({
      appKey: config.appKey || '',
      yotpoSecret: config.yotpoSecret || '',
      useYotpo: config.useYotpo !== false,
      fallbackRating: config.fallbackRating || '',
      reviewCountTemplate: config.reviewCountTemplate || '__COUNT__ ratings',
      reviewCountOneTemplate: config.reviewCountOneTemplate || '',
      averageScore: 0,
      totalReviews: 0,
      loading: true,
      loadError: false,

      init() {
        if (!this.useYotpo || !this.appKey) {
          this.loading = false;
          return;
        }

        loadStoreSummary(this.appKey, this.yotpoSecret)
          .then((summary) => {
            this.averageScore = summary.averageScore;
            this.totalReviews = summary.totalReviews;
          })
          .catch(() => {
            this.loadError = true;
          })
          .finally(() => {
            this.loading = false;
          });
      },

      get summaryReady() {
        return this.useYotpo && !this.loading && !this.loadError && this.totalReviews > 0;
      },

      get ratingValue() {
        if (this.summaryReady && this.averageScore > 0) {
          return Number(this.averageScore).toFixed(1);
        }

        return this.fallbackRating;
      },

      get reviewsLabel() {
        return formatReviewCountLabel(this.totalReviews, this.reviewCountTemplate, this.reviewCountOneTemplate);
      },
    }));
  }

  if (root.Alpine) {
    registerYotpoStoreSummary();
  } else {
    root.document.addEventListener('alpine:init', registerYotpoStoreSummary, { once: true });
  }
})();
